import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import * as R from 'Router/routes';
import { isAlreadyExistErrorMsg } from 'utils/errorMessageValidation';
import { applyAll, empty, notPhoneNumber } from 'utils/validations';
import { Button, Input, Link, Typography } from 'components';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import {
  formatBackendValidationErrors,
  PHONE_MASK,
  ValidationError,
} from 'utils';
import { usePhoneVerification /* useRecaptcha */ } from 'hooks/webapi';
import styles from './PhoneChange.module.scss';

type FormValues = {
  mobilePhone: string;
};

export default function PhoneChange() {
  const { t } = useTranslation(['phone_verification', 'common']);
  const [pending, setPending] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { changePhoneWithVerification } = usePhoneVerification();
  const [isFramedError, setIsFramedError] = useState(false);
  // const handleRecaptchaVerify = useRecaptcha('/client/phone');
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (
      { mobilePhone }: FormValues,
      formikHelpers: FormikHelpers<FormValues>,
    ) => {
      try {
        setPending(true);
        // const recaptchaToken = await handleRecaptchaVerify();
        const mobilePhoneSanitized = mobilePhone
          .replace(/ /g, '')
          .replace('+420', '');

        await changePhoneWithVerification(
          mobilePhoneSanitized,
          /* recaptchaToken */
        );

        navigate(R.PHONE_VERIFICATION);
      } catch (e: unknown) {
        if (e instanceof ValidationError && e?.fieldErrors?.length) {
          const isAlreadyExistInDbError = isAlreadyExistErrorMsg(e);
          setIsFramedError(isAlreadyExistInDbError);
          const errors = formatBackendValidationErrors(
            e.fieldErrors,
            'mobilePhone',
          );
          formikHelpers.setErrors(errors);
        }

        setPending(false);
      }
    },
    [changePhoneWithVerification /* handleRecaptchaVerify */, navigate],
  );

  const validateForm = useCallback(({ mobilePhone }: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    errors.mobilePhone = applyAll(
      empty,
      notPhoneNumber,
    )(mobilePhone.replace(/ /g, '').replace('+420', ''));

    const hasAnyError = Object.values(errors).filter(Boolean).length > 0;
    setDisabled(hasAnyError);

    return hasAnyError ? errors : {};
  }, []);

  const formik = useFormik({
    initialValues: {
      mobilePhone: '',
    },
    onSubmit: handleSubmit,
    validate: validateForm,
  });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsFramedError(false);
      formik.handleChange(event);
    },
    [formik],
  );

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Typography className={styles.title} size="xl" kind="h1">
          {t('change_number')}
        </Typography>
        <Typography className={styles.message} size="s">
          {t('insert_number')}
        </Typography>

        <form className={styles.form} onSubmit={formik.handleSubmit}>
          <Input
            type="tel"
            name="mobilePhone"
            autoComplete="tel"
            inputMode="tel"
            className={styles['mobile-phone-input']}
            value={formik.values.mobilePhone}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            isTouched={formik.touched.mobilePhone}
            placeholder={t('mobile')}
            error={formik.errors.mobilePhone}
            isFramedError={Boolean(formik.errors.mobilePhone) && isFramedError}
            mask={PHONE_MASK}
            guide
          />
          <Button
            disabled={!formik.touched.mobilePhone || disabled}
            className={styles.continue}
            pending={pending}
            type="submit"
          >
            {t('common:continue')}
          </Button>
          <div>
            <Link className={styles.link} to={R.PHONE_VERIFICATION}>
              <ChevronIcon className={styles.chevron} />
              {t('common:back')}
            </Link>
          </div>
        </form>
      </div>
    </>
  );
}
